import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OptionalSystemsState,
  StructureSystem,
  ModularHullSystem,
  PowerSystem,
  DriveSystem,
  FuelSystem,
  AccommodationSystem,
  BridgeSystem,
  CargoSystem,
  DroneSystem,
  SensorSystem,
  ExternalSystem,
  InternalSystem,
  SoftwareSystem,
} from "../../types/optionalSystems";

const initialState: OptionalSystemsState = {
  shipSize: 100,
  techLevel: 12,
  systems: [],
  structure: [],
  modularHull: [],
  power: [],
  drives: [],
  fuel: [],
  accommodations: [],
  bridge: [],
  cargo: [],
  drones: [],
  sensors: [],
  external: [],
  internal: [],
  software: [],
  commonAreas: [],
  errors: [],
  warnings: [],
  totalBandwidth: 0,
  totalSoftwareBandwidth: 0,
};

const optionalSystemsSlice = createSlice({
  name: "optionalSystems",
  initialState,
  reducers: {
    addStructureSystem: (state, action: PayloadAction<StructureSystem>) => {
      if (action.payload.subtype === "armouredBulkhead") {
        // Only one Armoured Bulkhead per ship; update if exists
        const existing = state.structure.find(
          (s) => s.subtype === "armouredBulkhead",
        );
        if (existing) {
          // Merge or replace protectedSystems and protectedTonnage
          existing.protectedSystems = action.payload.protectedSystems;
          existing.protectedTonnage = action.payload.protectedTonnage;
          existing.tonnage = action.payload.tonnage;
          existing.cost = action.payload.cost;
        } else {
          state.structure.push(action.payload);
        }
      } else {
        state.structure.push(action.payload);
      }
    },
    removeStructureSystem: (state, action: PayloadAction<string>) => {
      state.structure = state.structure.filter(
        (system) => system.id !== action.payload,
      );
    },
    addModularHullSystem: (state, action: PayloadAction<ModularHullSystem>) => {
      // Only one Modular Hull config per ship; replace if exists
      if (state.modularHull.length > 0) {
        state.modularHull[0] = action.payload;
      } else {
        state.modularHull.push(action.payload);
      }
    },
    removeModularHullSystem: (state, action: PayloadAction<string>) => {
      state.modularHull = state.modularHull.filter(
        (system) => system.id !== action.payload,
      );
    },
    addPowerSystem: (state, action: PayloadAction<PowerSystem>) => {
      state.power.push(action.payload);
    },
    removePowerSystem: (state, action: PayloadAction<string>) => {
      state.power = state.power.filter(
        (system) => system.id !== action.payload,
      );
    },
    addDriveSystem: (state, action: PayloadAction<DriveSystem>) => {
      state.drives.push(action.payload);
    },
    removeDriveSystem: (state, action: PayloadAction<string>) => {
      state.drives = state.drives.filter(
        (system) => system.id !== action.payload,
      );
    },
    addFuelSystem: (state, action: PayloadAction<FuelSystem>) => {
      state.fuel.push(action.payload);
    },
    removeFuelSystem: (state, action: PayloadAction<string>) => {
      state.fuel = state.fuel.filter((system) => system.id !== action.payload);
    },
    addAccommodationSystem: (
      state,
      action: PayloadAction<AccommodationSystem>,
    ) => {
      if (action.payload.subtype === "multiEnvironmentSpace") {
        // Only one MES system per ship; update if exists
        const existing = state.accommodations.find(
          (s) => s.subtype === "multiEnvironmentSpace",
        );
        if (existing) {
          // Merge or replace coveredSpaces
          existing.coveredSpaces = action.payload.coveredSpaces;
          existing.tonnage = action.payload.tonnage;
          existing.cost = action.payload.cost;
          existing.power = action.payload.power;
          existing.properties = action.payload.properties;
        } else {
          state.accommodations.push(action.payload);
        }
      } else {
        state.accommodations.push(action.payload);
      }
    },
    removeAccommodationSystem: (state, action: PayloadAction<string>) => {
      state.accommodations = state.accommodations.filter(
        (system) => system.id !== action.payload,
      );
    },
    updateAccommodationSystem: (
      state,
      action: PayloadAction<AccommodationSystem>,
    ) => {
      if (action.payload.subtype === "multiEnvironmentSpace") {
        const idx = state.accommodations.findIndex(
          (s) => s.subtype === "multiEnvironmentSpace",
        );
        if (idx !== -1) {
          state.accommodations[idx] = action.payload;
        }
      } else {
        const idx = state.accommodations.findIndex(
          (s) => s.id === action.payload.id,
        );
        if (idx !== -1) {
          state.accommodations[idx] = action.payload;
        }
      }
    },
    addBridgeSystem: (state, action: PayloadAction<BridgeSystem>) => {
      state.bridge.push(action.payload);
    },
    removeBridgeSystem: (state, action: PayloadAction<string>) => {
      state.bridge = state.bridge.filter(
        (system) => system.id !== action.payload,
      );
    },
    addCargoSystem: (state, action: PayloadAction<CargoSystem>) => {
      state.cargo.push(action.payload);
    },
    removeCargoSystem: (state, action: PayloadAction<string>) => {
      state.cargo = state.cargo.filter(
        (system) => system.id !== action.payload,
      );
    },
    addDroneSystem: (state, action: PayloadAction<DroneSystem>) => {
      state.drones.push(action.payload);
    },
    removeDroneSystem: (state, action: PayloadAction<string>) => {
      state.drones = state.drones.filter(
        (system) => system.id !== action.payload,
      );
    },
    addSensorSystem: (state, action: PayloadAction<SensorSystem>) => {
      state.sensors.push(action.payload);
    },
    removeSensorSystem: (state, action: PayloadAction<string>) => {
      state.sensors = state.sensors.filter(
        (system) => system.id !== action.payload,
      );
    },
    addExternalSystem: (state, action: PayloadAction<ExternalSystem>) => {
      state.external.push(action.payload);
    },
    removeExternalSystem: (state, action: PayloadAction<string>) => {
      state.external = state.external.filter(
        (system) => system.id !== action.payload,
      );
    },
    addInternalSystem: (state, action: PayloadAction<InternalSystem>) => {
      state.internal.push(action.payload);
    },
    removeInternalSystem: (state, action: PayloadAction<string>) => {
      state.internal = state.internal.filter(
        (system) => system.id !== action.payload,
      );
    },
    addSoftwareSystem: (state, action: PayloadAction<SoftwareSystem>) => {
      state.software.push(action.payload);
    },
    removeSoftwareSystem: (state, action: PayloadAction<string>) => {
      state.software = state.software.filter(
        (system) => system.id !== action.payload,
      );
    },
    addCommonAreaSystem: (
      state,
      action: PayloadAction<
        import("../../types/optionalSystems").CommonAreaSystem
      >,
    ) => {
      if (!Array.isArray(state.commonAreas)) {
        state.commonAreas = [];
      }
      state.commonAreas.push(action.payload);
    },
    removeCommonAreaSystem: (state, action: PayloadAction<string>) => {
      if (!Array.isArray(state.commonAreas)) {
        state.commonAreas = [];
        return;
      }
      state.commonAreas = state.commonAreas.filter(
        (system) => system.id !== action.payload,
      );
    },

    resetOptionalSystems: (state) => {
      return initialState;
    },

    // Load optional systems state
    loadOptionalSystems: (state, action: PayloadAction<OptionalSystemsState>) => {
      Object.assign(state, action.payload);
    },
  },
});

export const {
  addStructureSystem,
  removeStructureSystem,
  addModularHullSystem,
  removeModularHullSystem,
  addPowerSystem,
  removePowerSystem,
  addDriveSystem,
  removeDriveSystem,
  addFuelSystem,
  removeFuelSystem,
  addAccommodationSystem,
  removeAccommodationSystem,
  updateAccommodationSystem,
  addBridgeSystem,
  removeBridgeSystem,
  addCargoSystem,
  removeCargoSystem,
  addDroneSystem,
  removeDroneSystem,
  addSensorSystem,
  removeSensorSystem,
  addExternalSystem,
  removeExternalSystem,
  addInternalSystem,
  removeInternalSystem,
  addSoftwareSystem,
  removeSoftwareSystem,
  addCommonAreaSystem,
  removeCommonAreaSystem,
  resetOptionalSystems,
  loadOptionalSystems,
} = optionalSystemsSlice.actions;

export default optionalSystemsSlice.reducer;
